
import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import Chip from '@mui/material/Chip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

// Third Party
import { useMutation } from '@apollo/client';
import { Grammarly, GrammarlyEditorPlugin } from "@grammarly/editor-sdk-react";
import { User as Auth0User } from '@auth0/auth0-react';
import { CircularProgress } from '@mui/material';
import parse from 'html-react-parser';
import { Player } from '@lottiefiles/react-lottie-player';
// Internal
import RichTextEditor from '../../components/RichTextEditor';
import { GENERATE_ART_FROM_POETRY, CREATE_PUBLICATION } from '../../data/graphql/mutations';
import { PublicationKindEnum } from '../../data/types';
import en from '../../data/en.json';
import MultipleSelectChip from '../../components/MultipleSelectChip';
import Success from '../../assets/animations/success.json';
import Error from '../../assets/animations/error.json';

interface GenerateArtFromPoemStepType {
  step: Number
  label: String
}

interface VerseCanvasProps {
  user: Auth0User
}

export default function VerseCanvas(props: VerseCanvasProps) {
  const steps: GenerateArtFromPoemStepType[] = [
    { step: 0, label: en.aiTools.generateArtFromPoetry.submitPoetry},
    { step: 1, label: en.aiTools.generateArtFromPoetry.generateArt},
    { step: 2, label: en.aiTools.generateArtFromPoetry.enterPublicationInfo},
    { step: 3, label: en.aiTools.generateArtFromPoetry.submitPublication},
  ]

  const [createPublication, { error: CreatePublicationError, data: CreatePublicationData }] 
  = useMutation(CREATE_PUBLICATION);

  const [generateArtFromPoetry, { error: GenerateArtFromPoetryError, data: GenerateArtFromPoetryData }] 
  = useMutation(GENERATE_ART_FROM_POETRY);

const [publicationTitle, setPublicationTitle] = React.useState<string>("");
  const [publicationHeadline, setPublicationHeadline] = React.useState<string>("");
  const [activeStep, setActiveStep] = React.useState<number>(0);
  const [poetry, setPoetry] = React.useState<string>("");
  const [artStyle, setArtStyle] = React.useState<string>("");
  const [artToolStyle, setArtToolStyle] = React.useState<string>("");
  const [isGeneratingArt, setIsGeneratingArt] = React.useState<boolean>(false);
  const [publicationType, setPublicationType] = 
    React.useState<PublicationKindEnum>(PublicationKindEnum.multimedia);
  const [imageUrl, setImageUrl] = React.useState<string>("");
  const [tagNames, setTagNames] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (GenerateArtFromPoetryData) {
      setIsGeneratingArt(false)
      const url = GenerateArtFromPoetryData.generateArtFromPoetry.url
      setImageUrl(url);
    }
  }, [GenerateArtFromPoetryData])

  React.useEffect(() => {
    if (GenerateArtFromPoetryError) {
      setIsGeneratingArt(false)
    }
  }, [GenerateArtFromPoetryError])

  function GenerateArtFromPoetrySteppers() {
    return (
      <Box sx={{ m: { xs: 0.5, md: 1 } }}>
        <Stepper 
          activeStep={activeStep} 
          alternativeLabel
          sx={{
            '& .MuiStepLabel-label': {
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              lineHeight: { xs: 1.2, md: 1.43 }
            }
          }}
        >
          {steps.map((step: GenerateArtFromPoemStepType, index: number) => (
            <Step key={index}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    );
  }

  const handleSubmitPublication = () => {
    createPublication({ variables: { 
      title: publicationTitle,
      headline: publicationHeadline,
      user_auth0_id: props.user?.sub,
      tag_names: tagNames,
      kind: publicationType,
      format: "online", // Default to online for VerseCanvas generated publications
      painting_url: imageUrl, // if painting_url is undefined, convert to null
      html_content: poetry // if html_content is undefined, convert to null
     }
    })
  }

  function SubmitPoetryStep() {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          px: { xs: 1, md: 2 },
          py: { xs: 2, md: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-around",
            m: 1,
            width: '100%',
            maxWidth: { xs: '100%', md: '600px' },
            gap: { xs: 2, md: 0 }
          }}>
          <Select
            value={artStyle}
            onChange={(e) => setArtStyle(e.target.value)}
            displayEmpty
            inputProps={{ 'aria-label': 'Without label' }}
            sx={{
              mr: { xs: 0, md: 2 },
              width: { xs: '100%', md: 'auto' },
              minWidth: { xs: '100%', md: 200 }
            }}
          >
            <MenuItem value="">
              <em>{en.aiTools.generateArtFromPoetry.artStyle.default}</em>
            </MenuItem>  
            <MenuItem value={en.aiTools.generateArtFromPoetry.artStyle.cubism}>
              {en.aiTools.generateArtFromPoetry.artStyle.cubism}
            </MenuItem>
            <MenuItem value={en.aiTools.generateArtFromPoetry.artStyle.abstract}>
              {en.aiTools.generateArtFromPoetry.artStyle.abstract}
            </MenuItem>
            <MenuItem value={en.aiTools.generateArtFromPoetry.artStyle.expressionism}>
              {en.aiTools.generateArtFromPoetry.artStyle.expressionism}
            </MenuItem>
            <MenuItem value={en.aiTools.generateArtFromPoetry.artStyle.impressionism}>
              {en.aiTools.generateArtFromPoetry.artStyle.impressionism}
            </MenuItem>
            <MenuItem value={en.aiTools.generateArtFromPoetry.artStyle.popArt}>
              {en.aiTools.generateArtFromPoetry.artStyle.popArt}
            </MenuItem>
            <MenuItem value={en.aiTools.generateArtFromPoetry.artStyle.surrealism}>
              {en.aiTools.generateArtFromPoetry.artStyle.surrealism}
            </MenuItem>
          </Select>

          <Select
            value={artToolStyle}
            onChange={(e) => setArtToolStyle(e.target.value)}
            displayEmpty
            inputProps={{ 'aria-label': 'Without label' }}
            sx={{
              width: { xs: '100%', md: 'auto' },
              minWidth: { xs: '100%', md: 200 }
            }}
          >
            <MenuItem value="">
              <em>{en.aiTools.generateArtFromPoetry.artToolStyle.default}</em>
            </MenuItem>
            <MenuItem value={en.aiTools.generateArtFromPoetry.artToolStyle.charcoal}>
              {en.aiTools.generateArtFromPoetry.artToolStyle.charcoal}
            </MenuItem>
            <MenuItem value={en.aiTools.generateArtFromPoetry.artToolStyle.inkSketch}>
              {en.aiTools.generateArtFromPoetry.artToolStyle.inkSketch}
            </MenuItem>
            <MenuItem value={en.aiTools.generateArtFromPoetry.artToolStyle.oilPainting}>
              {en.aiTools.generateArtFromPoetry.artToolStyle.oilPainting}
            </MenuItem>
            <MenuItem value={en.aiTools.generateArtFromPoetry.artToolStyle.pastel}>
              {en.aiTools.generateArtFromPoetry.artToolStyle.pastel}
            </MenuItem>
            <MenuItem value={en.aiTools.generateArtFromPoetry.artToolStyle.pencilSketch}>
              {en.aiTools.generateArtFromPoetry.artToolStyle.pencilSketch}
            </MenuItem>
            <MenuItem value={en.aiTools.generateArtFromPoetry.artToolStyle.watercolor}>
              {en.aiTools.generateArtFromPoetry.artToolStyle.watercolor}
            </MenuItem>
          </Select>
        </Box>

        <Box sx={{ width: '100%', maxWidth: { xs: '100%', md: '750px' } }}>
          <Grammarly
            clientId={process.env.REACT_APP_GRAMMARLY_API_KEY}
            config={{
              documentDialect: "american",
              autocomplete: "on",
            }}
          >
            <GrammarlyEditorPlugin
              clientId={process.env.REACT_APP_GRAMMARLY_API_KEY}
              config={{
                documentDialect: "american",
                autocomplete: "on",
              }}
            >
              <RichTextEditor
                value={poetry}
                onChange={(content) => setPoetry(content)}
                placeholder="Write your poetry here..."
                height={500}
                width="100%"
              />
            </GrammarlyEditorPlugin>
          </Grammarly>
        </Box>
      </Box>
    );
  }

  function GenerateArtStep() {
    return (
      <Box
        sx={{
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center", 
          pt: 2,
          px: { xs: 1, md: 2 },
          flexDirection: "column"
        }}>
          {isGeneratingArt && 
            <React.Fragment>
              <Typography 
                variant="h6" 
                sx={{
                  mr: { xs: 0, md: 1 },
                  mb: { xs: 1, md: 0 },
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  textAlign: 'center'
        }}
      >
              {en.aiTools.generateArtFromPoetry.generatingArt}
            </Typography>
            <CircularProgress size={30}/>
          </React.Fragment>
        }
        {!isGeneratingArt && imageUrl && 
          <Box sx={{ 
            width: '100%', 
            maxWidth: { xs: '100%', md: '600px' },
            display: 'flex',
            justifyContent: 'center'
          }}>
            <img 
              src={imageUrl} 
              alt="generated art"
              style={{
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }}
            />
          </Box>
        }
      </Box>
    )
  }

  function EnterPublicationInfoStep() {
    const publicationKinds: PublicationKindEnum[] = [
      PublicationKindEnum.literature, 
      PublicationKindEnum.multimedia, 
      PublicationKindEnum.visualArt,
    ]

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 3 },
        }}
      >
        <Typography 
          variant="h4" 
          sx={{ 
            p: 1,
            fontSize: { xs: '1.5rem', md: '2rem' },
            textAlign: 'center',
            lineHeight: { xs: 1.2, md: 1.167 }
          }}
        >
          {en.common.submitPublicationSection.enterPublicationInfo}
        </Typography>

        <FormControl sx={{ width: { xs: '100%', md: '50vw' }, maxWidth: '600px' }}>
          <Typography 
            variant="body1" 
            sx={{ 
              pt: 1,
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}
          >
            {en.common.submitPublicationSection.publicationTitleQuestion}
          </Typography>
          <TextField
            required
            id="filled-required"
            label="Required"
            defaultValue={publicationTitle}
            onChange={(e) => {
              setPublicationTitle(e.target.value);
            }}
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <Typography 
            variant="body1" 
            sx={{ 
              pt: 1,
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}
          >
            {
              en.common.submitPublicationSection
                .publicationHeadlineQuestion
            }
          </Typography>

          <TextField
            required
            id="filled-required"
            label="Required"
            defaultValue={publicationHeadline}
            onChange={(e) => {
              setPublicationHeadline(e.target.value);
            }}
            variant="outlined"
            sx={{ mb: 2 }}
          />

          <Typography 
            variant="body1" 
            sx={{ 
              pt: 1,
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}
          >
            {en.common.submitPublicationSection.addTagsToPublication}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <MultipleSelectChip tagNames={tagNames} setTagNames={setTagNames}/>
          </Box>

          <Typography 
            variant="body1" 
            sx={{ 
              pt: 1,
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}
          >
            {en.common.submitPublicationSection.publicationTypeQuestion}
          </Typography>
          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            defaultValue={publicationKinds[0]}
            name="radio-buttons-group"
            sx={{ mt: 1 }}
          >
            {publicationKinds.map(
              (publicationKind: PublicationKindEnum, index: number) => (
                <FormControlLabel
                  key={index}
                  value={publicationKind}
                  control={<Radio />}
                  label={publicationKind}
                  onChange={() => setPublicationType(publicationKind)}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: { xs: '0.9rem', md: '1rem' }
                    }
                  }}
                />
              )
            )}
          </RadioGroup>
        </FormControl>
      </Box>
    );
  }

  function SubmitPublicationStep() {
    return (
      <Box
        sx={{
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          flexDirection: "column", 
          mt: 3,
          px: { xs: 2, md: 3 }
        }}>
          <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", mt: 3}}>
            <Typography 
              variant='h4' 
              sx={{
                p: 1,
                fontSize: { xs: '1.5rem', md: '2rem' },
                textAlign: 'center',
                lineHeight: { xs: 1.2, md: 1.167 }
        }}
      >
            {en.common.submitPublicationSection.submitPublication}
          </Typography>
        </Box>

        <Box
          sx={{
            ml: { xs: 0, md: 10 },
            width: { xs: '100%', md: 'auto' },
            maxWidth: { xs: '100%', md: '600px' }
          }}>
            <Typography 
              variant='subtitle2' 
              sx={{
                pt: 1, 
                textTransform: 'capitalize',
                fontSize: { xs: '0.8rem', md: '0.875rem' }
          }}
        >
            {en.common.submitPublicationSection.publicationTitleQuestion}
          </Typography>
          <Typography 
            variant='body1' 
            sx={{
              pt: 1,
              fontSize: { xs: '0.9rem', md: '1rem' },
              wordWrap: 'break-word'
            }}
          >
            {publicationTitle}
          </Typography>

          <Typography 
            variant='subtitle2' 
            sx={{
              pt: 1, 
              textTransform: 'capitalize',
              fontSize: { xs: '0.8rem', md: '0.875rem' }
            }}
          >
            {en.common.submitPublicationSection.publicationHeadlineQuestion}
          </Typography>
          <Typography 
            variant='body1' 
            sx={{
              pt: 1,
              fontSize: { xs: '0.9rem', md: '1rem' },
              wordWrap: 'break-word'
            }}
          >
            {publicationHeadline}
          </Typography>
          <Typography 
            variant='subtitle2' 
            sx={{
              pt: 1, 
              textTransform: 'capitalize',
              fontSize: { xs: '0.8rem', md: '0.875rem' }
            }}
          >
            {en.common.submitPublicationSection.publicationTags}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {tagNames.map((tagName: string, index: number) => (
              <Chip 
                key={index} 
                label={tagName} 
                variant="outlined" 
                size={window.innerWidth < 600 ? "small" : "medium"}
              />
            ))}
          </Box>

          <Typography 
            variant='subtitle2' 
            sx={{
              pt: 1, 
              textTransform: 'capitalize',
              fontSize: { xs: '0.8rem', md: '0.875rem' }
            }}
          >
            {en.common.submitPublicationSection.publicationContent}
          </Typography>
          <Box sx={{ 
            mt: 1, 
            '& *': { 
              fontSize: { xs: '0.9rem', md: '1rem' },
              lineHeight: { xs: 1.4, md: 1.5 }
            }
          }}>
            {poetry && parse(poetry)}
          </Box>
          {imageUrl && 
            <Box sx={{ 
              mt: 2, 
              display: 'flex', 
              justifyContent: 'center',
              width: '100%'
            }}>
              <img 
                width={Math.min(350, window.innerWidth - 32)} 
                src={imageUrl} 
                alt="painting"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}
              />
            </Box>
          }
          <FormGroup sx={{ mt: 2 }}>
            <FormControlLabel 
              required 
              control={<Checkbox />} 
              label={en.common.submitPublicationSection.acknowledgeTermsAndConditions}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: { xs: '0.85rem', md: '0.9rem' }
                }
              }}
            />
            <FormControlLabel 
              required 
              control={<Checkbox />} 
              label={en.common.submitPublicationSection.allowEditing}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: { xs: '0.85rem', md: '0.9rem' }
                }
              }}
            />
          </FormGroup>
        </Box>
      </Box>
    )
  }

  function handleGenerateArtFromPoetry() {
    setIsGeneratingArt(true)
    generateArtFromPoetry({
      variables: {
        poetry: poetry,
        user_auth0_id: props.user.sub,
        art_style: artStyle,
        art_tool_style: artToolStyle
      }
    })
  }

  function handleNextStep() {
    if (steps[activeStep+1].label === en.aiTools.generateArtFromPoetry.generateArt) {
      handleGenerateArtFromPoetry()
    }
    setActiveStep(activeStep + 1)
  }

  function GenerateArtFromPoetryNavigation() {
    return (
      <Box
        sx={{
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center", 
          pt: 2,
          px: { xs: 2, md: 0 },
          gap: { xs: 1, md: 2 },
          flexWrap: 'wrap'
        }}>
          { activeStep > 0 && 
            <Button 
              onClick={() => setActiveStep(activeStep - 1)}
              sx={{
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                px: { xs: 2, md: 3 },
                py: { xs: 1, md: 1.5 }
        }}
      >
            {en.common.previous}
          </Button> 
        }
        { (activeStep < steps.length - 1) && 
          <Button 
            onClick={() => handleNextStep()}
            sx={{
              fontSize: { xs: '0.8rem', md: '0.875rem' },
              px: { xs: 2, md: 3 },
              py: { xs: 1, md: 1.5 }
            }}
          >
            {en.common.next}
          </Button> 
        }
        { (activeStep === steps.length - 1) && 
          <Button 
            onClick={handleSubmitPublication}
            sx={{
              fontSize: { xs: '0.8rem', md: '0.875rem' },
              px: { xs: 2, md: 3 },
              py: { xs: 1, md: 1.5 }
            }}
          >
            {en.common.complete}
          </Button> 
        }
      </Box>
    )
  }

  if (CreatePublicationData !== undefined) {
    return (
      <Player
        autoplay={true}
        loop={true}
        src={Success}
        style={{ height: '300px', width: '300px' }}
      />
    )
  }

  if (CreatePublicationError !== undefined || GenerateArtFromPoetryError !== undefined) {
    return (
      <Player
        autoplay={true}
        loop={true}
        src={Error}
        style={{ height: '300px', width: '300px' }}
      />
    )
  }

  return (
    <Box
      sx={{
        px: { xs: 1, md: 2 },
        py: { xs: 2, md: 3 },
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden'
      }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '2rem', md: '2.75rem' },
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
      }}
    >
          VerseCanvas
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            mt: 1,
            color: 'text.secondary',
            fontSize: { xs: '0.95rem', md: '1.1rem' },
          }}
        >
          Transform your poetry into visual art with AI
        </Typography>
      </Box>

      {GenerateArtFromPoetrySteppers()}

      {activeStep === 0 && SubmitPoetryStep()}
      {activeStep === 1 && GenerateArtStep()}
      {activeStep === 2 && EnterPublicationInfoStep()}
      {activeStep === 3 && SubmitPublicationStep()}

      {GenerateArtFromPoetryNavigation()}
    </Box>
  );
};
